import React, { useCallback, useMemo } from 'react';
import { Box, Button, TextField } from '@mui/material';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

interface Values {
  chequeBookId: string;
  maxChequeNumber: string;
}

const validationSchema = yup.object().shape({
  chequeBookId: yup.string().required(),
  maxChequeNumber: yup.number().required().integer().min(1),
});

const useChequeBookForm = (
  defaultValues: Values,
  onSubmit: (values: Values) => void,
) => {
  const form = useForm<Values>({
    resolver: yupResolver(validationSchema) as any,
    defaultValues,
    mode: 'onBlur',
  });

  const handleFormSubmit = useMemo(
    () => form.handleSubmit(onSubmit),
    [form, onSubmit],
  );

  const resetValues = useCallback(
    (values: Values) => {
      form.reset(values);
    },
    [form],
  );

  return useMemo(
    () => ({
      form,
      handleFormSubmit,
      resetValues,
    }),
    [form, handleFormSubmit, resetValues],
  );
};

export interface Props {
  form: ReturnType<typeof useChequeBookForm>;
  cancellable?: boolean;
  onCancelClicked?: () => void;
}

const Component = (props: Props) => {
  const {
    form: { form, handleFormSubmit },
    cancellable,
    onCancelClicked,
  } = props;
  const { register, formState } = form;
  const { errors } = formState;

  return (
    <Box
      component="form"
      sx={{
        textAlign: 'right',
        '& > *': { margin: (theme) => theme.spacing(1, 1) },
      }}
      onSubmit={handleFormSubmit}
    >
      <TextField
        {...register('chequeBookId')}
        autoFocus
        variant={'filled'}
        label={'Numéro du chéquier'}
        error={errors.chequeBookId != null}
        helperText={
          errors.chequeBookId != null ? 'Numéro de chéquier invalide' : ' '
        }
      />
      <TextField
        {...register('maxChequeNumber')}
        variant={'filled'}
        label={'Nombre de chèques'}
        error={errors.maxChequeNumber != null}
        helperText={errors.maxChequeNumber != null ? 'Nombre invalide' : ' '}
      />
      <br />
      {cancellable ? (
        <Button
          type={'button'}
          color={'primary'}
          variant={'outlined'}
          onClick={onCancelClicked}
        >
          Annuler
        </Button>
      ) : null}
      <Button type={'submit'} color={'primary'} variant={'contained'}>
        Valider
      </Button>
    </Box>
  );
};

export const AccountChequeBookResetForm = {
  Component,
  useForm: useChequeBookForm,
};

// eslint-disable-next-line @typescript-eslint/no-namespace
export declare namespace AccountChequeBookResetForm {
  export type FormValues = Values;
  export type ComponentProps = Props;
  export type FormState = ReturnType<typeof useChequeBookForm>;
}
